import { MONETIZATION_PENDING, type RecordDto } from '@halo/shared';
import { normalizeForComparison } from '../format';
import {
  applyChanges,
  compactData,
  readOptionalString,
  readString,
  type RecordData,
} from './parsing';

/**
 * Kiếm tiền là BỐN trạng thái:
 *   'on'         đã bật kiếm tiền
 *   'off'        CHƯA bật (giao diện ẩn kênh này khỏi phần doanh thu)
 *   'pending'    PENDING BKT — đã nộp chờ duyệt bật kiếm tiền (thêm 24/09/2026)
 *   'undeclared' CHƯA AI KHAI
 *
 * «Chưa ai khai» phải tách hẳn khỏi «chưa bật»: 154 kênh đang có đều chưa khai — coi chúng như
 * «chưa bật» là cả phần doanh thu trống trơn. Chưa khai thì KHÔNG bị ẩn.
 *
 * «Pending BKT» cũng không bị ẩn: kênh đang chờ duyệt chưa có tiền nhưng cũng không phải kênh bỏ
 * đó — người phụ trách còn phải theo dõi nó cho tới lúc được duyệt.
 */
export type Monetization = 'on' | 'off' | 'pending' | 'undeclared';

export const MONETIZATION_LABELS: Record<Monetization, string> = {
  on: 'Đã bật',
  off: 'Chưa bật',
  pending: 'Pending BKT',
  undeclared: '? kiếm tiền',
};

/** Bốn trạng thái theo thứ tự đọc tự nhiên: đã bật → đang chờ duyệt → chưa bật → chưa ai khai. */
export const MONETIZATIONS: readonly Monetization[] = ['on', 'pending', 'off', 'undeclared'];

/**
 * Nhãn dùng chữ BKT như trong phòng vẫn gọi nhau — cho ô lọc và file Excel. Chip trong bảng giữ chữ của
 * `MONETIZATION_LABELS` («Đã bật» / «Chưa bật»): chip nằm ngay cạnh số liệu nên chữ ngắn dễ đọc hơn.
 */
export const MONETIZATION_BKT_LABELS: Record<Monetization, string> = {
  on: 'Đã BKT',
  off: 'Chưa BKT',
  pending: 'Pending BKT',
  undeclared: 'Chưa khai',
};

/**
 * Tình trạng của kênh. Kênh «Tạm dừng / Bị gậy / Đã bán» vẫn giữ nguyên số liệu cũ — trạng thái chỉ
 * để bảng khỏi lẫn kênh chết vào kênh đang chạy (và để «độ đầy dữ liệu» không đòi số của kênh chết).
 * Mã là DỮ LIỆU (khoá `tt` trong bản ghi) nên giữ nguyên chữ của bản cũ.
 */
export const CHANNEL_STATUSES = ['chay', 'dung', 'gay', 'ban'] as const;
export type ChannelStatus = (typeof CHANNEL_STATUSES)[number];
export const DEFAULT_CHANNEL_STATUS: ChannelStatus = 'chay';

export const CHANNEL_STATUS_LABELS: Record<ChannelStatus, string> = {
  chay: 'Đang chạy',
  dung: 'Tạm dừng',
  gay: 'Bị gậy',
  ban: 'Đã bán',
};

/** Giá trị lạ / vắng mặt đều là «đang chạy» — đúng cách máy chủ hiểu. */
export function readChannelStatus(value: unknown): ChannelStatus {
  return (CHANNEL_STATUSES as readonly unknown[]).includes(value)
    ? (value as ChannelStatus)
    : DEFAULT_CHANNEL_STATUS;
}

/** Bấm nhãn trạng thái là đổi vòng: đang chạy → tạm dừng → bị gậy → đã bán → đang chạy. */
export function nextChannelStatus(status: ChannelStatus): ChannelStatus {
  const index = CHANNEL_STATUSES.indexOf(status);
  return CHANNEL_STATUSES[(index + 1) % CHANNEL_STATUSES.length] ?? DEFAULT_CHANNEL_STATUS;
}

export interface Channel {
  recordId: number;
  owner: string | null;
  /** Mã ổn định «k3@user» — video trỏ tới mã này, doanh thu cũng lưu theo mã này. */
  code: string;
  name: string;
  url: string;
  topic: string;
  monetization: Monetization;
  /** Tên CMS nhận tiền. `undefined` = chưa gán. */
  cms: string | undefined;
  status: ChannelStatus;
  /**
   * ĐIỀU KHOẢN ĐỐI TÁC (thêm 26/09/2026) — chỉ Giám đốc và cấp phòng đọc được. Máy chủ cắt hẳn bốn
   * khoá này khỏi bản ghi gửi cho người khác, nên ở máy họ bốn ô luôn là chuỗi rỗng; giao diện cũng
   * không vẽ ra (xem `canSeeChannelTerms`).
   */
  /**
   * ẨN KÊNH khỏi mọi danh sách và ô chọn (thêm 26/09/2026).
   *
   * Công ty có những cặp kênh TRÙNG do trước đây bàn giao bằng cách lập kênh mới thay vì chuyển —
   * bản cũ vẫn nằm đó làm rối danh sách. Ẩn KHÔNG phải xoá: video và doanh thu đã gắn với mã kênh
   * ấy còn nguyên, và bật lại lúc nào cũng được. Vì vậy đây là cách dọn AN TOÀN cho một bản ghi vẫn
   * mang dữ liệu thật.
   */
  hidden: boolean;
  contract: string;
  broker: string;
  /** Tỷ lệ ăn chia, để nguyên văn: «70/30», «60%», «70 công ty – 30 đối tác». */
  share: string;
  paymentMethod: string;
  /**
   * ĐỐI TÁC giữ kênh (thêm 29/09/2026, khoá `dt`) — cùng nhóm điều khoản đối tác: máy chủ cắt khỏi
   * bản ghi gửi cho người ngoài cấp phòng. Chọn từ danh sách Giám đốc khai ở Cài đặt, gõ ngoài
   * danh sách vẫn được; «Thông tin kênh» lọc được theo đối tác.
   */
  partner: string;
  /**
   * VĂN PHÒNG / NHÓM GẮN THẲNG cho kênh (28/09/2026), khoá `vp` / `nh`.
   *
   * Kênh vốn không có phòng riêng: nó thừa hưởng phòng/nhóm của NGƯỜI PHỤ TRÁCH. Nhưng Giám đốc
   * không thuộc phòng nào, mà «những kênh Giám đốc lập là kênh của Giám đốc» — vẫn phải xếp được
   * vào một văn phòng (Phòng Nhạc Việt chưa có nhân sự nào) để bảng kênh và doanh thu gom đúng chỗ.
   *
   * Nên hai ô này CHỈ CÓ GIÁ TRỊ khi người phụ trách không thuộc phòng nào; kênh của nhân viên
   * vẫn theo người, hai ô để trống — chuyển người là chuyển phòng, như trước.
   */
  departmentId: string;
  teamId: string;
  /**
   * MAIL TÀI KHOẢN GIỮ KÊNH và NGÀY NHẬN KÊNH (29/09/2026) — hai cột chủ dự án muốn thấy ngay trên
   * bảng kênh, cạnh người phụ trách. Mail chỉ là tên đăng nhập của kênh; mật khẩu và 2FA là hai ô
   * riêng của chủ kênh bên dưới (mục «Lưu trữ» của Giám đốc là một sổ khác, luật quyền khác).
   */
  ownerEmail: string;
  /** «DD/MM/YYYY» như mọi ngày khác trong app; '' = chưa ghi. */
  receivedOn: string;
  /**
   * MẬT KHẨU (`pw`) và MÃ BÍ MẬT 2FA (`otp`) của tài khoản giữ kênh (29/09/2026, chủ dự án: «mật khẩu,
   * 2FA (ẩn ký tự) chỉ có chủ tài khoản nhập mới xem được»). Máy chủ chỉ gửi hai khoá này cho chính
   * NGƯỜI PHỤ TRÁCH kênh và cắt hẳn với mọi người khác — người khác nhận '' và cũng không ghi được
   * (`CHANNEL_OWNER_SECRET_KEYS`). Bảng kênh che ký tự, chủ kênh bấm mới hiện.
   */
  password: string;
  twoFactor: string;
}

/**
 * Đọc được CẢ HAI nguồn: khoá `km` của bản ghi (1 / 0 / 'pending' / vắng mặt) và giá trị máy chủ
 * gửi về trong DTO doanh thu (true / false / 'pending' / null).
 */
export function readMonetization(value: unknown): Monetization {
  if (value === true || value === 1) return 'on';
  if (value === false || value === 0) return 'off';
  if (value === MONETIZATION_PENDING) return 'pending';
  return 'undeclared';
}

export function parseChannel(record: RecordDto): Channel {
  const data = record.data;
  return {
    recordId: record.id,
    owner: record.owner,
    code: readString(data, 'id') || `legacy:${record.id}`,
    name: readString(data, 'name'),
    url: readString(data, 'url'),
    topic: readString(data, 'topic'),
    monetization: readMonetization(data.km),
    cms: readOptionalString(data, 'cms'),
    status: readChannelStatus(data.tt),
    hidden: data.an === 1 || data.an === true,
    contract: readString(data, 'hd'),
    broker: readString(data, 'tg'),
    share: readString(data, 'tl'),
    paymentMethod: readString(data, 'pay'),
    partner: readString(data, 'dt'),
    departmentId: readString(data, 'vp'),
    teamId: readString(data, 'nh'),
    ownerEmail: readString(data, 'mail'),
    receivedOn: readString(data, 'nhan'),
    password: readString(data, 'pw'),
    twoFactor: readString(data, 'otp'),
  };
}

/**
 * Phòng / nhóm HIỆU LỰC của kênh: gắn thẳng nếu có, không thì theo người phụ trách. Cùng một luật
 * với máy chủ (`channelInfoOf` trong module doanh thu) — sửa bên này nhớ sửa bên kia.
 */
export function channelUnit(
  channel: Pick<Channel, 'departmentId' | 'teamId'>,
  owner: { departmentId: string; teamId: string } | undefined,
): { departmentId: string; teamId: string } {
  return {
    departmentId: channel.departmentId || owner?.departmentId || '',
    teamId: channel.teamId || owner?.teamId || '',
  };
}

/**
 * Giá trị ghi xuống khoá `km`. Bản cũ chỉ có 1 / 0, nên «Pending BKT» lưu bằng CHỮ: bản web cũ đọc
 * không ra thì rơi về «chưa khai» — đúng phía an toàn, không thành «đã bật» hay «chưa bật».
 */
const MONETIZATION_KEY: Record<Monetization, 1 | 0 | string | undefined> = {
  on: 1,
  off: 0,
  pending: MONETIZATION_PENDING,
  undeclared: undefined,
};

/**
 * VẮNG MẶT phải thật sự vắng mặt: không bao giờ ghi `0`/`null`/`''` cho «chưa khai» hay «chưa
 * gán CMS» — máy chủ và các màn doanh thu phân biệt khoá vắng mặt với giá trị sai.
 */
function toChannelData(channel: Channel): RecordData {
  return {
    id: channel.code,
    name: channel.name,
    url: channel.url,
    topic: channel.topic,
    km: MONETIZATION_KEY[channel.monetization],
    cms: channel.cms || undefined,
    // «Đang chạy» là mặc định của khoá vắng mặt → không ghi, bản ghi giữ gọn như bản cũ tạo ra.
    tt: channel.status === DEFAULT_CHANNEL_STATUS ? undefined : channel.status,
    // Kênh đang hiện là mặc định → khoá vắng mặt, bản ghi giữ gọn.
    an: channel.hidden ? 1 : undefined,
    // Điều khoản đối tác — xem `Channel`. Ô trống thì khoá vắng mặt, như mọi khoá khác.
    hd: channel.contract.trim() || undefined,
    tg: channel.broker.trim() || undefined,
    tl: channel.share.trim() || undefined,
    pay: channel.paymentMethod.trim() || undefined,
    dt: channel.partner.trim() || undefined,
    vp: channel.departmentId || undefined,
    nh: channel.teamId || undefined,
    mail: channel.ownerEmail.trim() || undefined,
    nhan: channel.receivedOn.trim() || undefined,
    // Khoá riêng của chủ kênh: người khác không nhận về nên cũng không gửi lên — máy chủ giữ phần đang lưu.
    pw: channel.password.trim() || undefined,
    otp: channel.twoFactor.replace(/\s+/g, '') || undefined,
  };
}

/**
 * Tên kênh có vướng gì không (tách ra 26/09/2026 để kiểm thử được):
 *
 *   • `taken`     — ĐỔI tên sang tên một kênh khác đang dùng. CHẶN: đó là tự tạo trùng lặp mới.
 *   • `duplicate` — GIỮ NGUYÊN một tên vốn đã trùng sẵn. CHỈ NHẮC, không chặn.
 *   • `ok`        — không vướng gì.
 *
 * Phân biệt hai trường hợp ấy là cần thiết: công ty có sẵn những cặp kênh trùng tên (hai «PINOY
 * REGGAE» cùng Phòng KD05). Chặn cả trường hợp thứ hai là KHOÁ LUÔN hộp «Sửa kênh» của những kênh
 * ấy — không sửa nổi CMS, người phụ trách hay điều khoản đối tác, chỉ vì một cái tên có từ trước mà
 * người sửa không hề đụng tới.
 */
export type ChannelNameCheck = 'ok' | 'taken' | 'duplicate';

export function checkChannelName(
  name: string,
  existing: readonly Pick<Channel, 'recordId' | 'name'>[],
  editing?: Pick<Channel, 'recordId' | 'name'>,
): ChannelNameCheck {
  const wanted = normalizeForComparison(name);
  if (!wanted) return 'ok';
  const taken = existing.some(
    (channel) =>
      channel.recordId !== editing?.recordId && normalizeForComparison(channel.name) === wanted,
  );
  if (!taken) return 'ok';
  return editing && normalizeForComparison(editing.name) === wanted ? 'duplicate' : 'taken';
}

/** Có `stored` (đang SỬA): chỉ ghi đè khoá thật sự đổi — xem `applyChanges`. Không có: tạo mới. */
export function serializeChannel(channel: Channel, stored?: RecordDto): RecordData {
  const next = toChannelData(channel);
  if (!stored) return compactData(next);
  return compactData(applyChanges(stored.data, toChannelData(parseChannel(stored)), next));
}

/**
 * Mã cho KÊNH mới «k<số>@<người tạo>». Từ 22/09/2026 kênh đổi được người phụ trách, nên người tạo có thể
 * không còn THẤY mọi kênh mang «@tên mình» — đếm tiếp từ số lớn nhất đang thấy (như `nextLocalCode`) là có
 * thể sinh lại một mã đã có, và doanh thu / video gắn theo mã sẽ bị trộn vào nhau. Nên phần số là thời
 * điểm tạo (giây), và vẫn lớn hơn mọi số đang thấy (hai kênh tạo trong cùng một giây vẫn khác mã).
 */
export function newChannelCode(
  existingCodes: readonly string[],
  username: string,
  nowMs: number,
): string {
  const highest = existingCodes.reduce((max, code) => {
    const match = /^k(\d+)/.exec(code);
    return match ? Math.max(max, Number(match[1])) : max;
  }, 0);
  return `k${Math.max(highest + 1, Math.floor(nowMs / 1000))}@${username}`;
}

export interface ChannelOwnerGroup {
  username: string;
  channels: Channel[];
}

/**
 * Gom kênh theo NGƯỜI PHỤ TRÁCH, người giữ nhiều kênh nhất lên trước — danh sách chọn của ô «người
 * đang giữ kênh» khi bàn giao cả cụm (25/09/2026). Kênh không có chủ (tài khoản đã bị xoá) không
 * vào nhóm nào: không có ai để bàn giao HỘ, phải sửa từng kênh.
 */
export function channelsByOwner(channels: readonly Channel[]): ChannelOwnerGroup[] {
  const groups = new Map<string, ChannelOwnerGroup>();
  for (const channel of channels) {
    if (!channel.owner) continue;
    const group = groups.get(channel.owner) ?? { username: channel.owner, channels: [] };
    group.channels.push(channel);
    groups.set(channel.owner, group);
  }
  return [...groups.values()].sort(
    (a, b) => b.channels.length - a.channels.length || a.username.localeCompare(b.username),
  );
}

export function isValidUrl(url: string): boolean {
  return /^https?:\/\/\S+$/i.test(url);
}

/** «https://www.youtube.com/@ReggaeLoveSongs/videos» → «@ReggaeLoveSongs». */
export function channelNameFromUrl(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return '';
  }
  const segments = parsed.pathname.split('/').filter(Boolean);
  const handle = segments.find((segment) => segment.startsWith('@'));
  if (handle) return handle;
  const [first, second] = segments;
  if (first && second && ['c', 'user', 'channel'].includes(first)) return second;
  return first ?? parsed.hostname.replace(/^www\./, '');
}

export type ChannelLookup = (code: string | undefined) => Channel | undefined;

/**
 * Tra kênh theo mã. Mã CHẾT (kênh đã xoá / ngoài phạm vi) trả `undefined` → video rơi vào nhóm
 * «chưa gắn kênh». Luôn tra qua hàm này, đừng kiểm `!video.channelCode`: mã chết vẫn là chuỗi.
 */
export function buildChannelLookup(channels: readonly Channel[]): ChannelLookup {
  const byCode = new Map(channels.map((channel) => [channel.code, channel]));
  return (code) => (code ? byCode.get(code) : undefined);
}
